import React, {useState} from "react";
import {Alert, Button, Modal, Paper, PasswordInput, TextInput} from "@mantine/core";
import AppHeader from "../header/AppHeader";
import {Container, Row} from "react-bootstrap";
import {IconLock, IconUser} from "@tabler/icons-react";
import UseraccountService from "../../services/useraccount.service";
import {useNavigate} from "react-router-dom";
import md5 from 'md5';
import Cookies from "js-cookie";
import "../../css/views/Login.css"
import * as Sentry from "@sentry/react";

export default function Login() {
    const navigate = useNavigate();
    const [nameValue, setNameValue] = useState('');
    const [passwordValue, setPasswordValue] = useState('');
    const [showAlert, setAlert] = useState(false);
    const [showModalAlert, setModalAlert] = useState(false);
    const [showChangePwModal, setShowChangePwModal] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [repeatPassword, setRepeatPassword] = useState('');
    const [pendingUser, setPendingUser] = useState<any>(null);

    /**
     * Event handler for the change password modal.
     */
    async function handleChangePassword() {
        if (!pendingUser) return;

        if (!newPassword || newPassword === nameValue || newPassword !== repeatPassword) {
            setModalAlert(true);
            return; // Do not proceed if the new password is empty, matches the username, or does not match the repeat password
        }

        pendingUser.password = md5(newPassword);
        const result = await UseraccountService.update(pendingUser, pendingUser.id)
            .catch((e: Error) => {
                Sentry.captureException(e, {
                    extra: {
                        request: {
                            pendingUser: JSON.parse(JSON.stringify(pendingUser)),
                        },
                    },
                });
            });

        if (!result) {
            setModalAlert(true);
            return;
        }

        // Reset the state after changing the password
        setShowChangePwModal(false);
        setNewPassword('');
        setRepeatPassword('');
        setPendingUser(null);
        setPasswordValue(newPassword); // Set the new password to the password input field
        setModalAlert(false);
    }

    /**
     * Gets the inputs, converts the clear password to md5 and checks if a user with this data
     * exists in the database.
     */
    async function tryLogin() {
        try {
            const loginData = (await UseraccountService.checkLogin({
                username: nameValue,
                password: md5(passwordValue)
            })).data;

            const dbUser = loginData.user;

            if (dbUser && dbUser.active) {
                Cookies.set('username', dbUser.username);
                Sentry.setUser({
                    username: Cookies.get("username")
                });

                const payload = JSON.parse(atob(loginData.token.split('.')[1]));
                const expiryDate = new Date(payload.exp * 1000); // convert from Unix seconds to JS Date
                Cookies.set('token', loginData.token, {expires: expiryDate});

                if (nameValue == passwordValue) { //Username and password entered are the same and user exists = new user
                    setPendingUser(dbUser);
                    setShowChangePwModal(true);
                    return;
                }

                setAlert(false);

                if (dbUser.role?.name === 'Admin' || dbUser.role?.name === 'DSS Leiter*in') {
                    navigate('/management', {replace: true})
                } else {
                    navigate('/home', {replace: true})
                }
            } else {
                setAlert(true);
            }
        } catch (error: any) {
            if (error.response?.status === 401) {
                setAlert(true);
                return;
            }
            throw error;
        }
    }

    /**
     * Event handler for key event that should only act on enter key presses.
     * when the pressed key is the enter key
     * @param event key down event
     */
    function keyDownEvent(event: React.KeyboardEvent<HTMLInputElement>) {
        const target = event.target as HTMLInputElement;

        if (event.key !== "Enter") {
            return;
        }

        if (target.id === "t_username" || target.id === "t_password") {
            if (!nameValue) {
                setAlert(true);
                return; // Do not proceed if username is empty
            }
            tryLogin();
        } else if (target.id === "t_new_password" || target.id === "t_repeat_password") {
            handleChangePassword();
        }
    }

    return (
        <div id="loginPage">
            <AppHeader showHomeIcon={true} showNewPlanIcon={false}
                       showUserIcon={true} showStartpageIcon={false} showBigLogo={false}/>
            <Container className="app-content-container">
                <div id="loginWindow">
                    <Paper shadow="sm" p="xl" withBorder={true} w="500px" ta={"center"}>
                        <Row className="firstRow heading">
                            <h1>Anmeldung</h1>
                        </Row>
                        <TextInput id={"t_username"}
                                   icon={<IconUser size="0.8rem"/>}
                                   value={nameValue}
                                   onChange={(event) => setNameValue(event.currentTarget.value)}
                                   placeholder="Benutzername"
                                   onKeyDown={(event) => keyDownEvent(event)}
                        />
                        <PasswordInput id={"t_password"} pt={"10px"}
                                       icon={<IconLock size="0.8rem"/>}
                                       value={passwordValue}
                                       onChange={(event) => setPasswordValue(event.currentTarget.value)}
                                       placeholder="Passwort"
                                       onKeyDown={(event) => keyDownEvent(event)}
                        />
                        {
                            showAlert ?
                                <Alert id={'loginAlert'} w={"100%"} title="Einloggen fehlgeschlagen" ta={"left"}
                                       color="red" mt={"10px"}>
                                    Bitte überprüfen Sie Benutzername und Passwort!
                                </Alert> : <></>
                        }
                        <Button onClick={tryLogin} ta={"center"} mt={"10px"}>
                            Anmelden
                        </Button>
                    </Paper>
                </div>
            </Container>
            <Modal
                opened={showChangePwModal}
                onClose={() => {
                }}
                title="Passwort ändern"
                centered
                closeOnEscape={false} // Prevent closing on escape key
                closeOnClickOutside={false} // Prevent closing on outside click
                withCloseButton={false} // Hide close button
            >
                <div style={{marginBottom: 16}}>
                    Bitte ändern Sie Ihr Passwort. Das neue Passwort darf nicht mit dem Benutzernamen übereinstimmen.
                </div>
                <PasswordInput
                    label="Neues Passwort"
                    id={"t_new_password"}
                    value={newPassword}
                    onChange={e => setNewPassword(e.currentTarget.value)}
                    onKeyDown={(event => {
                        keyDownEvent(event)
                    })}
                    placeholder="Neues Passwort"
                    mt="md"
                />
                <PasswordInput
                    label="Neues Passwort wiederholen"
                    value={repeatPassword}
                    id={"t_repeat_password"}
                    onChange={e => setRepeatPassword(e.currentTarget.value)}
                    onKeyDown={(event => {
                        keyDownEvent(event)
                    })}
                    placeholder="Neues Passwort wiederholen"
                    mt="md"
                />
                {
                    showModalAlert ?
                        <Alert id={'modalAlert'} w={"100%"} title="Passwortänderung fehlgeschlagen" ta={"left"}
                               color="red" mt={"10px"}>
                            Das Passwort darf nicht leer sein, darf nicht gleich sein wie der Benutzername und beide
                            Eingaben müssen übereinstimmen.
                        </Alert> : <></>
                }
                <Button
                    mt="md"
                    fullWidth
                    onClick={handleChangePassword}
                >
                    Passwort speichern
                </Button>
            </Modal>
        </div>
    );
};